/**
 * Script backend factories registered in the {@link BackendRegistry}.
 *
 * Each resolves an `assetId` to a runnable executor: {@link TypeScriptBackend} loads a
 * compiled Behaviour module, {@link RedprintBackend} wraps a graph. Asset *resolution*
 * (manifest/VFS) is injected, so these are testable without the player runtime.
 */

import {
  TypeScriptExecutor,
  loadBehaviour,
  type BehaviourCtor,
  type IScriptExecutor,
  type IScriptExecutorFactory,
} from "@engine/core";
import type { Graph, NodeRegistry } from "@engine/redprint";
import { GraphInterpreter } from "./GraphInterpreter";

/**
 * TypeScript backend: compiles-on-load a Behaviour module via {@link loadBehaviour} (the
 * source is the editor-swc output) and runs it through {@link TypeScriptExecutor}. Loaded
 * Behaviour constructors are cached per assetId.
 */
export class TypeScriptBackend implements IScriptExecutorFactory {
  readonly scriptType = "typescript" as const;
  private readonly _cache = new Map<string, BehaviourCtor>();

  constructor(
    /** Resolve an assetId to its compiled (CommonJS) JS source. */
    private readonly _getSource: (assetId: string) => string | null,
    /** Virtual modules the script may `require` (e.g. `{ '@engine/core': {...} }`). */
    private readonly _modules: Record<string, unknown>,
  ) {}

  create(assetId: string): IScriptExecutor {
    let ctor = this._cache.get(assetId);
    if (!ctor) {
      const source = this._getSource(assetId);
      if (source == null) throw new Error(`[TypeScriptBackend] no source for '${assetId}'`);
      ctor = loadBehaviour(source, this._modules);
      this._cache.set(assetId, ctor);
    }
    return TypeScriptExecutor.fromClass(ctor);
  }

  /** Drop a cached Behaviour ctor (e.g. on hot-reload of its source). */
  invalidate(assetId: string): void {
    this._cache.delete(assetId);
  }
}

/** Redprint backend: resolves an assetId to a graph and runs it via {@link GraphInterpreter}. */
export class RedprintBackend implements IScriptExecutorFactory {
  readonly scriptType = "redprint" as const;

  constructor(
    private readonly _getGraph: (assetId: string) => Graph | null,
    private readonly _registry: NodeRegistry,
  ) {}

  create(assetId: string): IScriptExecutor {
    const graph = this._getGraph(assetId);
    if (!graph) throw new Error(`[RedprintBackend] no graph for '${assetId}'`);
    return new GraphInterpreter(graph, this._registry);
  }
}
